import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, Loader2, Mail, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/components/AuthProvider';
import { ConnectionStatus } from '@/components/auth/ConnectionStatus';
import { supabase } from '@/integrations/supabase/client';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

type Step = 'email' | 'profile' | 'verify';

interface ProfileData {
  firstName: string;
  lastName: string;
  country: string;
}

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "EU", name: "European Union" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "JP", name: "Japan" },
  { code: "IN", name: "India" },
  { code: "OTHER", name: "Other" },
];

export default function UserFlowEntry() {
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [justSignedUp, setJustSignedUp] = useState(false);
  
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    country: '',
  });
  
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const detectCountry = () => {
      try {
        const locale = navigator.language || 'en-US';
        const countryCode = locale.split('-')[1]?.toUpperCase();
        
        const countryMap: Record<string, string> = {
          'US': 'US', 'GB': 'GB', 'UK': 'GB', 'CA': 'CA', 'AU': 'AU',
          'NZ': 'NZ', 'JP': 'JP', 'IN': 'IN',
          'DE': 'EU', 'FR': 'EU', 'IT': 'EU', 'ES': 'EU', 'NL': 'EU',
          'BE': 'EU', 'AT': 'EU', 'PT': 'EU', 'IE': 'EU', 'SE': 'EU',
          'DK': 'EU', 'FI': 'EU', 'GR': 'EU', 'PL': 'EU', 'CZ': 'EU'
        };
        
        setProfileData(prev => ({ ...prev, country: countryMap[countryCode || ''] || 'OTHER' }));
      } catch {
        setProfileData(prev => ({ ...prev, country: 'OTHER' }));
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    // Only redirect existing logged-in users, not those who just signed up
    if (!authLoading && user && !justSignedUp) {
      navigate('/userflow/go', { replace: true });
    }
  }, [user, authLoading, navigate, justSignedUp]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (otp.length === 6) {
      handleVerifyOTP();
    }
  }, [otp]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email) && email.trim().length > 0;
    setEmailError(isValid ? '' : 'Please enter a valid email address');
    return isValid;
  };

  const handleBack = () => {
    if (step === 'verify') {
      setStep('profile');
      setOtp('');
    } else if (step === 'profile') {
      setStep('email');
    } else {
      navigate('/');
    }
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) return;
    setStep('profile');
  };

  const handleProfileContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileData.firstName.trim()) {
      toast({ title: 'First name required', variant: 'destructive' });
      return;
    }
    if (!profileData.country) {
      toast({ title: 'Please select your country', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          data: {
            first_name: profileData.firstName.trim(),
            last_name: profileData.lastName.trim(),
            country: profileData.country,
          },
        },
      });
      if (error) throw error;
      toast({ title: 'Code sent!', description: 'Check your email for the verification code.' });
      setStep('verify');
      setResendCooldown(60);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to send verification code', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    setJustSignedUp(true); // Prevent auto-redirect
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: otp,
        type: 'email',
      });
      if (error) throw error;
      
      // Save first name for onboarding flow
      localStorage.setItem('signup_first_name', profileData.firstName.trim());
      
      toast({ title: 'Welcome!', description: 'Signed in successfully.' });
      // Navigate to onboarding flow
      navigate('/userflow/go', { replace: true });
    } catch {
      toast({ title: 'Invalid code', description: 'Please check the code and try again.', variant: 'destructive' });
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          data: {
            first_name: profileData.firstName.trim(),
            last_name: profileData.lastName.trim(),
            country: profileData.country,
          },
        },
      });
      if (error) throw error;
      toast({ title: 'Code resent!', description: 'Check your email for the new code.' });
      setResendCooldown(60);
      setOtp('');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to resend code', variant: 'destructive' });
    } finally {
      setResendLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) return null;

  const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  const steps: Step[] = ['email', 'profile', 'verify'];

  const renderContent = () => (
    <>
      {/* Header with back/close */}
      <div className="absolute top-3 left-3 right-3 z-20 flex justify-between pt-safe">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="w-10 h-10 p-0 rounded-full bg-white/10 backdrop-blur-xl border-0 hover:bg-white/20 transition-all"
        >
          {step === 'email' ? <X className="w-4 h-4 text-foreground" /> : <ArrowLeft className="w-4 h-4 text-foreground" />}
        </Button>
        <div className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all ${
                step === s ? 'bg-primary w-6' : i < steps.indexOf(step) ? 'bg-primary/60' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
        <div className="w-10" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-16 w-full overflow-auto">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20">
            <img 
              src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
              alt="BuxBox"
              className="h-14 w-auto"
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 'email' && (
            <motion.div
              key="email"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-6 w-full max-w-md"
            >
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-semibold text-foreground tracking-tight" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                  Let's Begin
                </h2>
                <p className="text-foreground/80" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                  Enter your email to continue
                </p>
              </div>
              <form onSubmit={handleEmailContinue} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50" />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 pl-12 bg-white/10 border-0 backdrop-blur-xl text-foreground placeholder:text-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
                    style={{ borderRadius: '14px / 18px' }}
                    disabled={loading}
                  />
                </div>
                {emailError && <p className="text-sm text-destructive px-1">{emailError}</p>}
                <div className="flex justify-center pt-4">
                  <Button
                    type="submit"
                    className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                    style={{ borderRadius: '14px / 18px' }}
                    disabled={loading || !email.trim()}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {step === 'profile' && (
            <motion.div
              key="profile"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-4 w-full max-w-md"
            >
              <div className="text-center space-y-4 mb-6">
                <h2 className="text-3xl font-semibold text-foreground tracking-tight" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                  Create Profile
                </h2>
                <p className="text-foreground/80 text-sm" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                  Tell us a bit about yourself
                </p>
              </div>
              <form onSubmit={handleProfileContinue} className="space-y-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50" />
                  <Input
                    type="text"
                    placeholder="First name"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                    className="w-full h-12 pl-12 bg-white/10 border-0 backdrop-blur-xl text-foreground placeholder:text-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
                    style={{ borderRadius: '14px / 18px' }}
                    disabled={loading}
                  />
                </div>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50" />
                  <Input
                    type="text"
                    placeholder="Last name (optional)"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                    className="w-full h-12 pl-12 bg-white/10 border-0 backdrop-blur-xl text-foreground placeholder:text-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
                    style={{ borderRadius: '14px / 18px' }}
                    disabled={loading}
                  />
                </div>
                <Select value={profileData.country} onValueChange={(value) => setProfileData({ ...profileData, country: value })} disabled={loading}>
                  <SelectTrigger className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl text-foreground focus:ring-0 focus:ring-offset-0" style={{ borderRadius: '14px / 18px' }}>
                    <SelectValue placeholder="Country/Region" />
                  </SelectTrigger>
                  <SelectContent className="bg-background/95 backdrop-blur-xl border border-white/10 z-50">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-foreground/60 text-center py-2">
                  By continuing, you agree to our{" "}
                  <a href="/terms-of-service" target="_blank" className="text-primary hover:underline">Terms & Conditions</a>
                </p>
                <div className="flex justify-center pt-2">
                  <Button
                    type="submit"
                    className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                    style={{ borderRadius: '14px / 18px' }}
                    disabled={loading || !profileData.firstName.trim()}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {step === 'verify' && (
            <motion.div
              key="verify"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="space-y-6 w-full max-w-md"
            >
              <div className="text-center space-y-3">
                <h2 className="text-3xl font-semibold text-foreground tracking-tight" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                  Enter Code
                </h2>
                <p className="text-foreground/80">We sent a code to</p>
                <p className="text-foreground font-medium">{email}</p>
              </div>
              <div className="flex flex-col items-center">
                <InputOTP value={otp} onChange={setOtp} maxLength={6} disabled={loading}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {loading && (
                  <div className="mt-6 flex items-center gap-2 text-foreground/70">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Verifying...</span>
                  </div>
                )}
                <div className="mt-8">
                  {resendCooldown > 0 ? (
                    <p className="text-sm text-foreground/50">Resend code in {resendCooldown}s</p>
                  ) : (
                    <Button variant="ghost" onClick={handleResendCode} disabled={resendLoading} className="text-primary hover:text-primary/80">
                      {resendLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Resend Code
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );

  return (
    <>
      <ConnectionStatus />
      <div className="fixed inset-0 z-50" style={{ overscrollBehavior: 'none' }}>
        {/* Mobile: Bottom sheet style */}
        <div className="sm:hidden h-full flex flex-col justify-end">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="h-[95vh] glass-morphism rounded-t-[24px] flex flex-col overflow-auto"
          >
            {/* Drag handle indicator */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-foreground/20" />
            </div>
            {renderContent()}
          </motion.div>
        </div>

        {/* Desktop: Centered card */}
        <div className="hidden sm:flex h-full items-center justify-center p-4">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md md:max-w-2xl glass-morphism rounded-lg border border-border/50 shadow-2xl relative"
          >
            {renderContent()}
          </motion.div>
        </div>
      </div>
    </>
  );
}
